import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChatWidget } from '../components/chat/ChatWidget';
import { ChatErrorBoundary } from '../components/error/ChatErrorBoundary';
import { Loader2 } from 'lucide-react';
import { useGetBackendUrl } from '../hooks/use-get-backend-url';

interface WorkflowInfo {
  id: string;
  name: string;
  title?: string;
  subtitle?: string;
  inputPlaceholder?: string;
  initialMessages?: string[];
}

/**
 * EmbedChatPage - Optimized for iframe embedding
 * This page renders the chat widget in fullscreen mode without any chrome
 * Perfect for embedding in external websites
 */
export default function EmbedChatPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  const backendUrl = useGetBackendUrl();
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get optional styling params from URL
  const primaryColor = searchParams.get('primaryColor');
  const fontFamily = searchParams.get('fontFamily');
  const borderRadius = searchParams.get('borderRadius');
  
  // Get session ID and initial messages flag from URL
  const sessionIdParam = searchParams.get('sessionId');
  const showInitialMessages = searchParams.get('showInitialMessages') === 'true';

  useEffect(() => {
    const fetchWorkflowInfo = async () => {
      if (!workflowId) {
        setError('No workflow ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch chat configuration (public endpoint, no auth required)
        const response = await fetch(`${backendUrl}/api/chat/${workflowId}/config`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to load chat');
        }

        const config = await response.json();
        
        if (!config.success) {
          throw new Error(config.error || 'Failed to load chat');
        }

        setWorkflowInfo({
          id: config.id,
          name: config.name,
          title: config.title,
          subtitle: config.subtitle,
          inputPlaceholder: config.inputPlaceholder,
          initialMessages: config.initialMessages,
        });
        
        setLoading(false);
      } catch (err: any) {
        // console.error('Error fetching chat config:', err);
        setError(err.message || 'Failed to load chat');
        setLoading(false);
      }
    };

    fetchWorkflowInfo();
  }, [workflowId, backendUrl]);

  if (loading) {
    return (
      <div className="h-screen w-full bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">Chat Unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!workflowInfo) {
    return null;
  }

  return (
    <div className="h-screen w-full bg-background overflow-hidden">
      <ChatErrorBoundary onReset={() => window.location.reload()}>
        <ChatWidget
          workflowId={workflowId!}
          apiEndpoint={backendUrl}
          mode="fullscreen"
          title={workflowInfo.title}
          subtitle={workflowInfo.subtitle}
          placeholder={workflowInfo.inputPlaceholder}
          initialMessages={showInitialMessages ? workflowInfo.initialMessages : undefined}
          className="h-full w-full"
          config={{
            workflowId: workflowId!,
            apiEndpoint: backendUrl,
            title: workflowInfo.title,
            subtitle: workflowInfo.subtitle,
            placeholder: workflowInfo.inputPlaceholder,
            initialMessages: showInitialMessages ? workflowInfo.initialMessages : undefined,
            primaryColor: primaryColor || undefined,
            fontFamily: fontFamily || undefined,
            borderRadius: borderRadius || undefined,
            sessionId: sessionIdParam || undefined,
          }}
        />
      </ChatErrorBoundary>
    </div>
  );
}
